import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Crown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import Avatar from "@/components/Avatar";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/how-it-works", label: "How It Works" },
  { to: "/books", label: "Our Books" },
  { to: "/events", label: "Meetups & Events" },
  { to: "/community", label: "The Community" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [light, setLight] = useState(false);
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 20);
      const els = document.elementsFromPoint(window.innerWidth / 2, 82);
      const sec = els.find((e) => e.dataset && e.dataset.navbg);
      setLight(sec?.dataset.navbg === "light");
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => { window.removeEventListener("scroll", onScroll); window.removeEventListener("resize", onScroll); };
  }, [pathname, user]);
  useEffect(() => { setOpen(false); }, [pathname]);

  const barBg = scrolled
    ? (light ? "bg-cream/90 backdrop-blur-md border-mahogany-700/15 py-2" : "bg-mahogany-900/92 backdrop-blur-md border-gold/20 py-2")
    : "bg-transparent border-transparent py-4";
  const accent = light ? "text-fuchsia" : "text-gold";
  const baseLink = light ? "text-mahogany-700/80 hover:text-mahogany-950" : "text-cream/80 hover:text-cream";
  const strongLink = light ? "text-mahogany-900 hover:text-fuchsia" : "text-cream/80 hover:text-cream";
  const underline = light ? "bg-fuchsia" : "bg-gold";

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 border-b ${barBg}`}
      data-testid="navbar"
    >
      <nav className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between">
        <Logo size="sm" />
        <div className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`relative text-[13px] uppercase tracking-[0.12em] transition-colors group ${
                pathname === l.to ? accent : baseLink
              }`}
            >
              {l.label}
              <span className={`absolute -bottom-1 left-0 h-px ${underline} transition-all duration-300 w-0 group-hover:w-full`} />
            </Link>
          ))}
        </div>
        <div className="hidden lg:flex items-center gap-3">
          {user ? (
            <>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="nav-avatar" className="rounded-full focus:outline-none"><Avatar src={user.picture} name={user.display_name || user.first_name} size={36} /></button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-mahogany-950 border border-gold/20 text-cream min-w-[180px]">
                  <DropdownMenuItem asChild><Link to="/profile" data-testid="nav-profile" className="cursor-pointer">My Profile</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild><Link to="/dashboard" data-testid="nav-dashboard" className="cursor-pointer">Dashboard</Link></DropdownMenuItem>
                  <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} data-testid="nav-logout" className="cursor-pointer text-fuchsia">Logout</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <Link to="/join" data-testid="nav-join-btn" className="btn-primary !px-6 !py-2.5">Join The Room</Link>
          )}
        </div>
        <button className={`lg:hidden ${light ? "text-mahogany-900" : "text-cream"}`} onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      {open && (
        <div className="lg:hidden bg-mahogany-950/98 backdrop-blur-md border-t border-gold/20 mt-2" data-testid="nav-mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <Link key={l.to} to={l.to} className="text-cream/90 uppercase tracking-[0.12em] text-sm py-1" data-testid={`mnav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{l.label}</Link>
            ))}
            <Link to="/terms" className="text-cream/90 uppercase tracking-[0.12em] text-sm py-1" data-testid="mnav-terms">Terms &amp; Conditions</Link>
            <div className="h-px bg-gold/20 my-2" />
            {user ? (
              <>
                {user.role === "admin" && <Link to="/admin" className="text-gold uppercase tracking-[0.12em] text-sm">Admin</Link>}
                <Link to="/dashboard" className="text-cream uppercase tracking-[0.12em] text-sm">Dashboard</Link>
                <Link to="/profile" className="text-cream uppercase tracking-[0.12em] text-sm">My Profile</Link>
                <button onClick={() => { logout(); navigate("/"); }} className="text-left text-cream/60 uppercase tracking-[0.12em] text-sm">Logout</button>
              </>
            ) : (
              <Link to="/join" className="btn-primary" data-testid="mnav-join-btn">Join The Room</Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
